import {
  NODE_RADIUS,
  CONNECTION,
  POSITIONING,
  CANVAS_SIZE
} from '../constants';
import type { Node, Connection } from '../types';

interface ArrowPoints {
  startX: number; startY: number;
  endX: number; endY: number;
  arrowPoint1X: number; arrowPoint1Y: number;
  arrowPoint2X: number; arrowPoint2Y: number;
}

export const calculateArrowPoints = (from: { x: number; y: number }, to: { x: number; y: number }): ArrowPoints => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const angle = Math.atan2(dy, dx);

  const startX = from.x + NODE_RADIUS * Math.cos(angle);
  const startY = from.y + NODE_RADIUS * Math.sin(angle);
  const endX = to.x - NODE_RADIUS * Math.cos(angle);
  const endY = to.y - NODE_RADIUS * Math.sin(angle);

  const arrowPoint1X = endX - CONNECTION.ARROW_SIZE * Math.cos(angle - CONNECTION.ARROW_ANGLE);
  const arrowPoint1Y = endY - CONNECTION.ARROW_SIZE * Math.sin(angle - CONNECTION.ARROW_ANGLE);
  const arrowPoint2X = endX - CONNECTION.ARROW_SIZE * Math.cos(angle + CONNECTION.ARROW_ANGLE);
  const arrowPoint2Y = endY - CONNECTION.ARROW_SIZE * Math.sin(angle + CONNECTION.ARROW_ANGLE);

  return { startX, startY, endX, endY, arrowPoint1X, arrowPoint1Y, arrowPoint2X, arrowPoint2Y };
};

export const calculateNodePosition = (
  type: string,
  existingNodes: Node[],
  connections: Connection[],
  parentNode: Node | null = null
): { x: number; y: number } => {
  const centerX = CANVAS_SIZE / 2;
  const centerY = CANVAS_SIZE / 2;

  if (type === 'problem') {
    const problems = existingNodes.filter(n => n.type === 'problem');
    return { x: centerX + (problems.length * POSITIONING.PROBLEM_SPACING) - 150, y: centerY };
  }

  const anchor = parentNode || existingNodes.filter(n => n.type === 'problem').pop();
  if (!anchor) return { x: centerX + POSITIONING.SOLUTION_RADIUS, y: centerY };

  const childCount = existingNodes.filter(n =>
    connections.some(c => c.from === anchor.id && c.to === n.id)
  ).length;

  const angle = POSITIONING.START_ANGLE + (childCount * POSITIONING.ANGLE_STEP);

  return {
    x: anchor.x + POSITIONING.SOLUTION_RADIUS * Math.cos(angle),
    y: anchor.y + POSITIONING.SOLUTION_RADIUS * Math.sin(angle)
  };
};

export const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number =>
  Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));

export const centerCanvasOnNodes = (nodes: Node[], container: HTMLElement, zoom = 1): void => {
  if (nodes.length === 0 || !container) return;
  const centerX = nodes.reduce((sum, n) => sum + n.x, 0) / nodes.length;
  const centerY = nodes.reduce((sum, n) => sum + n.y, 0) / nodes.length;
  container.scrollTo({
    left: centerX * zoom - container.clientWidth / 2,
    top: centerY * zoom - container.clientHeight / 2,
    behavior: 'smooth'
  });
};

export const centerCanvasInitial = (container: HTMLElement, zoom = 1): void => {
  if (!container) return;
  const centerX = (CANVAS_SIZE * zoom - container.clientWidth) / 2;
  const centerY = (CANVAS_SIZE * zoom - container.clientHeight) / 2;
  container.scrollLeft = Math.max(0, centerX);
  container.scrollTop = Math.max(0, centerY);
};

export const scrollToNode = (node: { x: number; y: number }, container: HTMLElement): void => {
  if (!container) return;
  container.scrollLeft = Math.max(0, node.x - container.clientWidth / 2);
  container.scrollTop = Math.max(0, node.y - container.clientHeight / 2);
};

type ChainDirection = 'right' | 'down' | 'diagonal';

export const calculateSequentialPositions = (
  parentNode: { x: number; y: number },
  count: number,
  direction: ChainDirection = 'diagonal'
): { x: number; y: number }[] => {
  const positions: { x: number; y: number }[] = [];
  const baseDistance = 200;
  const stepDistance = 180;

  for (let i = 0; i < count; i++) {
    let x: number, y: number;
    switch (direction) {
      case 'right':
        x = parentNode.x + baseDistance + (stepDistance * i);
        y = parentNode.y;
        break;
      case 'down':
        x = parentNode.x;
        y = parentNode.y + baseDistance + (stepDistance * i);
        break;
      case 'diagonal':
      default: {
        const distance = baseDistance + (stepDistance * i);
        x = parentNode.x + distance * 0.8;
        y = parentNode.y + distance * 0.4;
        break;
      }
    }
    positions.push({ x, y });
  }
  return positions;
};
